/* eslint-disable max-lines -- Pre-existing violation */
import {Show, VStack} from 'platform/foundation';
import {cn, cva} from 'platform/shared';
import {match} from 'ts-pattern';

import {
  CSSProperties,
  KeyboardEventHandler,
  MouseEvent,
  PointerEvent as ReactPointerEvent,
  useMemo,
  useRef,
} from 'react';

import {isNil, isNotNil} from 'ramda';
import {isArray, isNumber} from 'ramda-adjunct';

import {Nullish, precisionCalculation, suffixTestId, TestIdProps} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {rangeArray} from '../../utils/rangeArray';
import {HelperText} from '../HelperText/HelperText';
import {Label} from '../Label/Label';
import {DisplayValue, SliderTick, SliderValue} from './types';
import {getNonlinearValue} from './utils/getNonlinearValue';
import {getStep} from './utils/getStep';
import {getThumbLabelDisplay} from './utils/getThumbLabelDisplay';
import {roundToStep} from './utils/roundToStep';
import {toPercentage} from './utils/toPercentage';
import {withHalfTicks} from './utils/withHalfTicks';

type AllowMoveTrack<IsRange extends boolean = false> = IsRange extends true ? boolean : false;

export interface SliderProps<IsRange extends boolean = false>
  extends FormControlProps<SliderValue<IsRange>>,
    TestIdProps {
  min: number;
  max: number;
  onlyEdges?: boolean;
  step: number | number[];
  longStepCount?: number;
  isRange?: IsRange | Nullish;
  ticks?: boolean | number | SliderTick[];
  nonlinearTicks?: SliderTick[];
  allowMoveStart?: boolean;
  isAllowInverted?: boolean;
  displayValue?: DisplayValue;
  halfTicks?: boolean;
  allowMoveTrack?: AllowMoveTrack<IsRange>;
  isRainbow?: boolean;
  formatValue?: (value: number) => string;
  /** Called once per finished change: on drag release with the final value, and after a track click or key press. */
  onChangeEnd?: (value: SliderValue<IsRange>) => void;
}

const toNumberOrZero = (value: unknown): number => (isNil(value) ? 0 : Number(value));

const DEFAULT_PROPS = {
  allowMoveStart: true,
  min: 0,
  max: 100,
  longStepCount: 10,
  ticks: true,
  displayValue: 'off',
  halfTicks: false,
  formatValue: (x: number) => `${x}`,
} satisfies Partial<SliderProps>;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function Slider<IsRange extends boolean = false>(props: SliderProps<IsRange>) {
  const min = props.min ?? DEFAULT_PROPS.min;
  const max = props.max ?? DEFAULT_PROPS.max;
  const longStepCount = props.longStepCount ?? DEFAULT_PROPS.longStepCount;
  const ticks = props.ticks ?? DEFAULT_PROPS.ticks;
  const displayValue = props.displayValue ?? DEFAULT_PROPS.displayValue;
  const halfTicks = props.halfTicks ?? DEFAULT_PROPS.halfTicks;
  const formatValue = props.formatValue ?? DEFAULT_PROPS.formatValue;

  const trackRef = useRef<HTMLDivElement>(null);
  const startThumbRef = useRef<HTMLDivElement>(null);
  const endThumbRef = useRef<HTMLDivElement>(null);

  // Drag state does not affect rendering, and pointer capture keeps events on the initiating element.
  const draggedThumbRef = useRef<null | 'start' | 'end'>(null);
  const trackMoveStartPositionRef = useRef<number | null>(null);
  const draggedValueRef = useRef<SliderValue<IsRange> | null>(null);

  const resolveDragAnchor = () =>
    match(draggedThumbRef.current)
      .with('start', () => startThumbRef.current)
      .with('end', () => endThumbRef.current)
      .otherwise(() => null);

  const calculateValue = (clientX: number) => {
    if (!trackRef.current) {
      return;
    }

    const trackRect = trackRef.current.getBoundingClientRect();

    const clickOffset =
      Math.max(trackRect.x, Math.min(trackRect.x + trackRect.width, clientX)) - trackRect.x;

    let newValue = 0;
    if (props.nonlinearTicks) {
      const nonlinearValue = getNonlinearValue({
        min,
        max,
        clickOffset,
        nonlinearTicks: props.nonlinearTicks,
        trackWidth: trackRect.width,
      });

      newValue = roundToStep(nonlinearValue, props.step);
    } else {
      const value = (max - min) * (clickOffset / trackRect.width);

      newValue = roundToStep(min + value, props.step);
    }

    return newValue;
  };

  const updateValue = (clientX: number, end: boolean) => {
    if (props.isDisabled) {
      return;
    }

    const newValue = calculateValue(clientX);

    if (isNil(newValue)) {
      return;
    }

    const x = (old: number | [number, number]): number | [number, number] => {
      if (isArray(old)) {
        const newPair: [number, number] = end ? [old[0], newValue] : [newValue, old[1]];

        if (!props.isAllowInverted) {
          if (end && newValue < old[0]) {
            draggedThumbRef.current = 'start';
            return [newValue, old[0]];
          }

          if (!end && newValue > old[1]) {
            draggedThumbRef.current = 'end';
            return [old[1], newValue];
          }
        }

        return newPair;
      }

      return newValue;
    };

    const nextValue = x(props.value) as SliderValue<IsRange>;
    props.onChange?.(nextValue);

    return nextValue;
  };

  const commitValue = (value: SliderValue<IsRange> | undefined) => {
    if (isNotNil(value)) {
      props.onChangeEnd?.(value);
    }
  };

  const changeAndCommitValue = (value: SliderValue<IsRange>) => {
    props.onChange?.(value);
    props.onChangeEnd?.(value);
  };

  const onTrackClick = (event: MouseEvent) => {
    if (isNotNil(draggedThumbRef.current)) {
      return;
    }

    if (event.currentTarget.getAttribute('data-eagsliderdragged') === 'true') {
      event.currentTarget.setAttribute('data-eagsliderdragged', 'false');
      return;
    }

    if (!props.isRange) {
      return commitValue(updateValue(event.clientX, false));
    }

    const thumbs = [...event.currentTarget.querySelectorAll('.eag--slider__thumb')] as [
      Element,
      Element | undefined,
    ];

    const endThumb = thumbs[1];
    if (!endThumb) {
      // actually missing start thumb because of allowMoveStart
      return commitValue(updateValue(event.clientX, true));
    }

    const startRect = thumbs[0].getBoundingClientRect();
    const startCentre = startRect.left + (startRect.right - startRect.left) / 2;
    const startDiff = Math.abs(event.clientX - startCentre);

    const endRect = endThumb.getBoundingClientRect();
    const endCentre = endRect.left + (endRect.right - endRect.left) / 2;
    const endDiff = Math.abs(event.clientX - endCentre);

    commitValue(updateValue(event.clientX, endDiff <= startDiff));
  };

  // Pointer capture keeps move/up on the thumb, so a touch drag survives leaving the track and is not stolen as a scroll.
  const onDragStart = (thumb: 'start' | 'end') => (event: ReactPointerEvent<HTMLDivElement>) => {
    if (props.isDisabled || isNotNil(draggedThumbRef.current)) {
      return;
    }

    event.currentTarget.setPointerCapture?.(event.pointerId);
    trackRef.current?.setAttribute('data-eagsliderdragged', 'true');
    draggedThumbRef.current = thumb;
  };

  const onDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    const draggedThumb = draggedThumbRef.current;
    const dragAnchor = resolveDragAnchor();

    if (isNil(draggedThumb) || props.isDisabled || !trackRef.current) {
      return;
    }

    event.preventDefault();

    if (dragAnchor?.classList.contains('eag--slider__filled-track')) {
      const trackSizes = dragAnchor.getBoundingClientRect();
      const move = event.clientX - (trackMoveStartPositionRef.current ?? 0);

      const value1 = calculateValue(trackSizes.left + move);
      const value2 = calculateValue(trackSizes.left + trackSizes.width + move);

      const movedValue = [value1, value2] as SliderValue<IsRange>;
      trackMoveStartPositionRef.current = event.clientX;
      draggedValueRef.current = movedValue;
      props.onChange?.(movedValue);

      return;
    }

    draggedValueRef.current = updateValue(event.clientX, draggedThumb === 'end') ?? null;
  };

  // A drag that never moved the value has nothing to commit.
  const onDragStop = () => {
    const draggedValue = draggedValueRef.current;

    draggedThumbRef.current = null;
    trackMoveStartPositionRef.current = null;
    draggedValueRef.current = null;
    commitValue(draggedValue ?? undefined);
  };

  const dragHandlers = {
    onPointerMove: onDrag,
    onPointerUp: onDragStop,
    onPointerCancel: onDragStop,
  };

  const onKey: KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (props.isDisabled) {
      return;
    }

    const targetElement = event.target instanceof Element ? event.target : null;
    const valueIndex = targetElement?.id === 'thumb-end' ? 1 : 0;

    const selectedValue = isArray(props.value) ? props.value[valueIndex] : Number(props.value);

    let delta = 0;
    switch (event.code) {
      case 'Home':
        event.stopPropagation();
        return changeAndCommitValue(min as SliderValue<IsRange>);
      case 'End':
        event.stopPropagation();
        return changeAndCommitValue(max as SliderValue<IsRange>);
      case 'ArrowRight':
      case 'ArrowUp':
        event.stopPropagation();
        delta = getStep(selectedValue, props.step);
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        event.stopPropagation();
        delta = -getStep(selectedValue, props.step, 'prev');
        break;
      case 'PageUp':
        event.stopPropagation();
        delta = isArray(props.step)
          ? getStep(selectedValue, props.step)
          : precisionCalculation.multiply(props.step, longStepCount);
        break;
      case 'PageDown':
        event.stopPropagation();
        delta = -(isArray(props.step)
          ? getStep(selectedValue, props.step, 'prev')
          : precisionCalculation.multiply(props.step, longStepCount));
        break;
    }
    if (isNil(delta) || delta === 0) {
      return;
    }

    const shiftValue = (value: number) =>
      Math.max(min, Math.min(max, precisionCalculation.add(value, delta)));

    if (props.isRange) {
      const isEnd = valueIndex === 1;

      const nextValue: [number, number] = isEnd
        ? [(props.value as [number, number])[0], shiftValue((props.value as [number, number])[1])]
        : [shiftValue((props.value as [number, number])[0]), (props.value as [number, number])[1]];

      if (!props.isAllowInverted && nextValue[0] > nextValue[1]) {
        return;
      }

      changeAndCommitValue(nextValue as SliderValue<IsRange>);
    } else {
      const nextValue = shiftValue(props.value as number);
      changeAndCommitValue(nextValue as SliderValue<IsRange>);
    }
  };

  const valueStyles = props.isRange
    ? {
        sliderOffsetStart: toPercentage(
          min,
          max,
          (props.value as [number, number])[0],
          props.nonlinearTicks
        ),
        sliderOffsetEnd: toPercentage(
          min,
          max,
          (props.value as [number, number])[1],
          props.nonlinearTicks
        ),
        sliderFillSize: Math.abs(
          toPercentage(min, max, (props.value as [number, number])[1], props.nonlinearTicks) -
            toPercentage(min, max, (props.value as [number, number])[0], props.nonlinearTicks)
        ),
      }
    : {
        sliderOffset: toPercentage(min, max, props.value as number, props.nonlinearTicks),
      };

  const labelStyles = {
    thumbLabelDisplay: getThumbLabelDisplay(displayValue),
    thumbLabelDisplayHover: getThumbLabelDisplay(displayValue, true),
  };

  const disabledProps = props.isDisabled
    ? ({
        'data-disabled': '',
        'aria-disabled': 'true',
      } as const)
    : {};

  const computedTicks = useMemo<SliderTick[]>(() => {
    if (isNumber(ticks) && ticks < 0) {
      throw new Error('Cannot have negative count of ticks');
    }

    if (isNil(ticks) || ticks === false || ticks === 0) {
      return [];
    }

    let ticksToReturn: SliderTick[] =
      props.nonlinearTicks ?? (isArray(ticks) ? (ticks as SliderTick[]) : []);

    if (!props.nonlinearTicks && (ticks === true || isNumber(ticks))) {
      const ticksCount = ticks === true ? 3 : ticks;
      const step = ticksCount === 1 ? 0 : (max - min) / (ticksCount - 1);

      ticksToReturn = rangeArray(0, ticksCount).map((i) => ({
        value: min + step * i,
      }));
    }

    return halfTicks ? withHalfTicks(ticksToReturn) : ticksToReturn;
  }, [ticks, props.nonlinearTicks, halfTicks, max, min]);

  const onTrackDragStart = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (props.isDisabled || isNotNil(draggedThumbRef.current) || !props.allowMoveTrack) {
      return;
    }

    event.currentTarget.setPointerCapture?.(event.pointerId);
    trackRef.current?.setAttribute('data-eagsliderdragged', 'true');
    trackMoveStartPositionRef.current = event.clientX;
    draggedThumbRef.current = 'start';
  };

  const sliderCaseVal = isArray(props.value) // eslint-disable-line no-nested-ternary -- Pre-existing violation
    ? toNumberOrZero(props.value[1]) < toNumberOrZero(props.value[0])
      ? `${valueStyles.sliderOffsetEnd}%`
      : `${valueStyles.sliderOffsetStart}%`
    : '-10px';

  return (
    <VStack>
      <Label
        isRequired={props.isRequired}
        tooltip={props.tooltip}
        data-testid={suffixTestId('label', props)}
      >
        {props.label}
      </Label>
      <div
        className={cn(wrapperVariants())}
        data-testid={suffixTestId('sliderWrapper', props)}
        onKeyDown={onKey}
        role="slider" // eslint-disable-line jsx-a11y/role-has-required-aria-props -- Muted pre-existing violation
        {...disabledProps}
      >
        <div
          ref={trackRef}
          className={cn(trackVariants())}
          data-testid={suffixTestId('sliderTrack', props)}
          onClick={onTrackClick}
          {...disabledProps}
        >
          <div
            className={cn(unfilledTrackVariants())}
            data-testid={suffixTestId('sliderUnfilledTrack', props)}
            {...disabledProps}
          />
          <div
            className={cn(filledTrackVariants({allowMoveTrack: props.allowMoveTrack ?? false}))}
            style={getFilledTrackStyle({
              left: sliderCaseVal,
              value: props.value,
              sliderFillSize: valueStyles.sliderFillSize,
              sliderOffset: valueStyles.sliderOffset,
              isRainbow: props.isRainbow,
            })}
            data-testid={suffixTestId('sliderFilledTrack', props)}
            onPointerDown={onTrackDragStart}
            {...dragHandlers}
            {...disabledProps}
          />

          {props.isRange ? (
            <>
              <Show when={props.allowMoveStart}>
                <div
                  className={cn(thumbWrapperVariants())}
                  style={{left: `${valueStyles.sliderOffsetStart ?? 0}%`}}
                  data-testid={suffixTestId('sliderThumbStartWrapper', props)}
                  {...disabledProps}
                >
                  <div
                    ref={startThumbRef}
                    className={cn(thumbVariants())}
                    data-testid={suffixTestId('sliderThumbStart', props)}
                    onPointerDown={onDragStart('start')}
                    {...dragHandlers}
                    aria-valuemin={min}
                    aria-valuemax={max}
                    aria-valuenow={(props.value as number[])[0]}
                    aria-valuetext={formatValue((props.value as [number, number])[0])}
                    tabIndex={props.isDisabled ? -1 : 0}
                    {...disabledProps}
                  >
                    <div
                      className={cn(
                        'eag--slider__thumb-label',
                        thumbLabelVariants({isVisible: labelStyles.thumbLabelDisplay === 'block'})
                      )}
                    >
                      {formatValue((props.value as number[])[0])}
                    </div>
                  </div>
                </div>
              </Show>
              <div
                className={cn(thumbWrapperVariants())}
                style={{left: `${valueStyles.sliderOffsetEnd ?? 0}%`}}
                data-testid={suffixTestId('sliderThumbEndWrapper', props)}
                {...disabledProps}
              >
                <div
                  id="thumb-end"
                  ref={endThumbRef}
                  className={cn(thumbVariants())}
                  data-testid={suffixTestId('sliderThumbEnd', props)}
                  onPointerDown={onDragStart('end')}
                  {...dragHandlers}
                  aria-valuemin={min}
                  aria-valuemax={max}
                  aria-valuenow={(props.value as number[])[1]}
                  aria-valuetext={formatValue((props.value as [number, number])[1])}
                  tabIndex={props.isDisabled ? -1 : 0}
                  {...disabledProps}
                >
                  <div
                    className={cn(
                      'eag--slider__thumb-label',
                      thumbLabelVariants({isVisible: labelStyles.thumbLabelDisplay === 'block'})
                    )}
                  >
                    {formatValue((props.value as number[])[1])}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div
              className={cn(thumbWrapperVariants())}
              style={{left: `${valueStyles.sliderOffset ?? 0}%`}}
              data-testid={suffixTestId('sliderThumbSingleWrapper', props)}
              {...disabledProps}
            >
              <div
                ref={startThumbRef}
                className={cn(
                  thumbVariants({showLabelOnHover: labelStyles.thumbLabelDisplayHover === 'block'})
                )}
                data-testid={suffixTestId('sliderThumbSingle', props)}
                aria-valuemin={min}
                aria-valuemax={max}
                aria-valuenow={props.value as number}
                aria-valuetext={formatValue(props.value as number)}
                tabIndex={props.isDisabled ? -1 : 0}
                onPointerDown={onDragStart('start')}
                {...dragHandlers}
                {...disabledProps}
              >
                <div
                  className={cn(
                    'eag--slider__thumb-label',
                    thumbLabelVariants({isVisible: labelStyles.thumbLabelDisplay === 'block'})
                  )}
                >
                  {formatValue(props.value as number)}
                </div>
              </div>
            </div>
          )}
        </div>
        <Show when={computedTicks.length > 0}>
          <div
            className={cn(sliderTicksVariants())}
            data-testid={suffixTestId('sliderTicks', props)}
            {...disabledProps}
          >
            {computedTicks.map(({value, label, small}, index) => {
              const isFrist = index === 0;
              const isLast = index === computedTicks.length - 1;
              const tickPosition = match(value)
                .with(min, () => 'min' as const)
                .with(max, () => 'max' as const)
                .otherwise(() => undefined);
              const tickOffset = toPercentage(min, max, value, props.nonlinearTicks);

              return (
                <div
                  key={value}
                  className={cn(sliderTickVariants())}
                  style={getSliderTickStyle(tickPosition, tickOffset)}
                  data-testid={suffixTestId('sliderTick', props)}
                  {...disabledProps}
                >
                  <div
                    className={cn(tickIndicatorVariants({small: small ?? false}))}
                    style={getTickIndicatorStyle(tickPosition)}
                    data-testid={suffixTestId('sliderTickIndicator', props)}
                    {...disabledProps}
                  />
                  <div
                    className={cn(tickLabelVariants())}
                    data-testid={suffixTestId('sliderTickLabel', props)}
                    {...disabledProps}
                  >
                    {/* eslint-disable-next-line no-nested-ternary -- Pre-existing violation */}
                    {props.onlyEdges
                      ? isFrist || isLast
                        ? formatValue(value)
                        : null
                      : (label ?? formatValue(value))}
                  </div>
                </div>
              );
            })}
          </div>
        </Show>
      </div>
      <Show when={props.errorMessage ?? props.helperText}>
        <HelperText
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          data-testid={suffixTestId('helper', props)}
        />
      </Show>
    </VStack>
  );
}

const wrapperVariants = cva('select-none px-[10px] [&:disabled]:cursor-not-allowed');

const trackVariants = cva('relative z-10 mx-[10px] h-[20px] select-none [touch-action:none]');

const unfilledTrackVariants = cva(
  'absolute left-[-10px] top-1/2 h-[2px] w-[calc(100%+20px)] -translate-y-1/2 bg-[var(--color-palettes-neutral-40-100)]'
);

const filledTrackVariants = cva(
  'absolute top-1/2 h-[calc(var(--eag-space-step,0.25)*1rem)] -translate-y-1/2 select-none [touch-action:none]',
  {
    variants: {
      allowMoveTrack: {
        true: 'cursor-grab',
        false: '',
      },
    },
  }
);

type FilledTrackStyleArgs = {
  left: string;
  value: [number, number] | number;
  sliderFillSize?: number;
  sliderOffset?: number;
  isRainbow?: boolean;
};

const getFilledTrackStyle = (args: FilledTrackStyleArgs): CSSProperties => {
  const isInverted =
    isArray(args.value) && toNumberOrZero(args.value[1]) < toNumberOrZero(args.value[0]);
  const fillWidth = args.sliderFillSize ?? args.sliderOffset ?? 100;
  const baseStyle = {
    left: args.left,
    width: `${args.sliderFillSize ?? args.sliderOffset}%`,
  };

  if (!args.isRainbow) {
    return {
      ...baseStyle,
      backgroundColor: isInverted ? 'var(--color-severity-danger)' : 'var(--color-general-accent)',
    };
  }

  const backgroundPosition = isArray(args.value) ? getRainbowBackgroundPosition(args.value) : '0 0';

  return {
    ...baseStyle,
    background: isInverted
      ? 'var(--color-severity-danger)'
      : 'linear-gradient(90deg, var(--color-palettes-green-60-100) 0%, var(--color-palettes-yellow-60-100) 33%, var(--color-palettes-orange-60-100) 66%, var(--color-palettes-red-60-100) 100%)',
    backgroundSize: `calc(100% * 100 / ${fillWidth}) 100%`,
    backgroundPosition,
    backgroundRepeat: 'no-repeat',
  };
};

const getRainbowBackgroundPosition = (value: [number, number]) => {
  const left = Number(value[0]);
  const right = Number(value[1]);
  const divisor = left + (100 - right);
  const leftOffset = divisor === 0 ? 0 : (left / divisor) * 100;

  return `${leftOffset}% 0`;
};

const thumbWrapperVariants = cva('pointer-events-none absolute left-0 top-[21px] h-0 w-0');

const thumbVariants = cva(
  'pointer-events-auto h-[20px] w-[20px] cursor-pointer select-none rounded-[50%] border border-solid border-[var(--color-palettes-neutral-40-100)] bg-[var(--color-palettes-white-10-100)] [box-shadow:var(--shadow-elevation-2)] [border-image:initial] [touch-action:none] [transform:translate(-50%,-50%)_translateY(calc((-1*21px)/2))] focus:outline-none focus:[box-shadow:0_0_4px_1px_grey] focus-visible:[outline:2px_solid_var(--color-palettes-blue-40-100)] [&:disabled]:cursor-not-allowed [&:disabled]:border-[var(--color-palettes-neutral-20-100)]',
  {
    variants: {
      showLabelOnHover: {
        true: 'hover:[&_.eag--slider__thumb-label]:block',
        false: '',
      },
    },
    defaultVariants: {
      showLabelOnHover: false,
    },
  }
);

const thumbLabelVariants = cva(
  'absolute bottom-[calc(100%+8px)] left-1/2 -translate-x-1/2 whitespace-nowrap rounded-[var(--radius-x-small)] bg-[var(--color-palettes-neutral-900-100)] px-2 py-1 text-[length:var(--text-size-x-small)] font-[var(--font-weight-bold)] leading-[var(--text-leading-x-small)] text-[var(--color-palettes-white-10-100)]',
  {
    variants: {
      isVisible: {
        true: 'block',
        false: 'hidden',
      },
    },
  }
);

const sliderTicksVariants = cva('relative mx-[10px] h-[24px]');
const sliderTickVariants = cva('absolute top-0');

type TickPosition = 'min' | 'max' | undefined;

const getSliderTickStyle = (value: TickPosition, tickOffset: number): CSSProperties =>
  match(value)
    .with('min', () => ({transition: 'none', left: '-10px'}))
    .with('max', () => ({transform: 'translateX(-100%)', left: `calc(${tickOffset}% + 10px)`}))
    .with(undefined, () => ({transform: 'translateX(-50%)', left: `${tickOffset}%`}))
    .exhaustive();

const tickIndicatorVariants = cva(
  'absolute bottom-[calc(100%+10px)] w-px translate-y-1/2 bg-[var(--color-palettes-neutral-40-100)]',
  {
    variants: {
      small: {
        true: 'h-[8px]',
        false: 'h-[12px]',
      },
    },
  }
);

const getTickIndicatorStyle = (value: TickPosition): CSSProperties =>
  match(value)
    .with('min', () => ({left: 0}))
    .with('max', () => ({left: 'auto', right: 0}))
    .with(undefined, () => ({left: '50%'}))
    .exhaustive();

const tickLabelVariants = cva('whitespace-nowrap');
